import { useEffect, useMemo, useState } from 'preact/hooks'
import { lowerSafe } from '@/utils/strings'
import { getDeploymentStatusLabel, getDeploymentStatusRank } from '@/components/webs/constants/deploymentMeta'
import type { LandingPageDeploymentDto, DeploymentStatus } from '@/types/landingPage'

export interface DeploymentSearchFilter {
  searchQuery: string
  setSearchQuery: (value: string) => void
  statusFilter: string
  setStatusFilter: (value: string) => void
  /** `deployments` narrowed by the text search and the status filter. */
  filteredDeployments: LandingPageDeploymentDto[]
  /** Statuses present in the list, in lifecycle order — the Status dropdown options. */
  availableStatuses: DeploymentStatus[]
}

/**
 * ORB-501: client-side search + status filter over an already-loaded deployment list. Deployment
 * history is no longer pruned nightly, so a long-lived page accumulates entries indefinitely and
 * the grid needs a way to narrow them.
 *
 * The whole list arrives in one payload (`GET .../LandingPage/Deployments` takes no paging
 * parameters), so this filters in memory with no round-trip — the same shape as
 * useAssetSearchFilter.
 *
 * Search covers deploy number, content version, the status LABEL as rendered, and a failure's
 * error text: "the failed one", "#12", "v4" are the ways people actually look for a deploy. The
 * status dropdown is the more useful control of the two and offers only statuses present in the
 * list, falling back to "all" if the selected one stops existing (e.g. a rollback changes the last
 * Failed row) so the grid can never get stuck showing nothing.
 */
export function useDeploymentSearchFilter(
  deployments: LandingPageDeploymentDto[],
): DeploymentSearchFilter {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const availableStatuses = useMemo(() => {
    const present = Array.from(new Set(deployments.map((d) => d.Status)))
    return present.sort((a, b) => getDeploymentStatusRank(a) - getDeploymentStatusRank(b) || a.localeCompare(b))
  }, [deployments])

  useEffect(() => {
    if (statusFilter !== 'all' && !availableStatuses.includes(statusFilter as DeploymentStatus)) {
      setStatusFilter('all')
    }
  }, [availableStatuses, statusFilter])

  const filteredDeployments = useMemo(() => {
    // Leading '#' and 'v' are stripped so the numbers read back the way the grid prints them
    // ("#12", "v4") rather than forcing the user to guess the bare value. Only when what follows
    // is purely digits, so an error message starting with a 'v' ("validation failed") still
    // searches on its real text.
    const raw = lowerSafe(searchQuery).trim()
    const query = /^[#v]\d+$/.test(raw) ? raw.slice(1) : raw
    return deployments.filter((deployment) => {
      const matchesStatus = statusFilter === 'all' || deployment.Status === statusFilter
      if (!matchesStatus) return false
      if (query === '') return true

      const haystack = [
        String(deployment.VersionNumber),
        String(deployment.SourceVersionNumber),
        getDeploymentStatusLabel(deployment.Status),
        deployment.Error ?? '',
      ]
      return haystack.some((field) => lowerSafe(field).includes(query))
    })
  }, [deployments, searchQuery, statusFilter])

  return { searchQuery, setSearchQuery, statusFilter, setStatusFilter, filteredDeployments, availableStatuses }
}
